<template>
  <div>
    <div class="row">
      <h1 class="col-12">Advanced Search</h1>
    </div>
    <div class="row">
      <div class="col-lg-3 mb-3">
        <div class="card">
          <div class="card-header bg-primary text-white d-flex">
            <span class="font-weight-bold">Search Filters</span>
            <span class="ml-auto c-pointer white-space-nowrap">
              <b-button
                class='button-row-spacing'
                variant='secondary'
                size='sm'
                @click="clearSearchCriteria">
                Clear
              </b-button>
              <b-button
                class='button-row-spacing'
                variant='secondary'
                size='sm'
                @click="applySearchCriteria">
                Apply
              </b-button>
            </span>
          </div>
          <div class="card-body">
            <div class="row label-text">
              Search by:
            </div>
            <div class="row">
              <select
                 data-vv-as="Search Entity Type"
                 class="form-control"
                 name="entityType"
                 v-model="entityType">
                <option value="Contact">Contact</option>
                <option value="Instrument">Instrument</option>
                <option value="Project">Project</option>
                <option value="Property">Property</option>
                <option value="Service">Service</option>
              </select>
            </div>
            <component
              :is="entityComponents[entityType].searchFacets"
              :radioButtonFacetKey="radioButtonFacetKey"
            />
          </div>
        </div>
      </div>
      <div class="col-lg-9">
        <div v-if="serverError" class="alert alert-danger" role="alert">
          {{serverError}}
        </div>
        <div class="card">
          <div class="card-header bg-primary text-white d-flex">
            <span class="font-weight-bold">{{ entityType }} Search Results</span>
              <span  class="ml-auto c-pointer white-space-nowrap">
                <span v-if="hasSearchResults() && entityType ==='Contact'">
                <JournalEntryModal
                  :modalId="'JournalEntryModal1'"/>
              </span>
              <span v-if="hasSearchResults() && entityType !=='Contact' && !isExporting"
                @click="exportResults">
                Export Results
              </span>
              <span  v-if="isExporting" className="d-flex align-items-center justify-content-center placeholder">
                <label>
                  Exporting...   <font-awesome-icon icon="spinner" size="1x" pulse />
                </label>

              </span>
            </span>
          </div>
          <LoadingSpinner v-if="searchResultsLoading" message ='Loading Search Results' size = '1x'>
          </LoadingSpinner>
          <div v-else-if='lastAppliedSearchHadNoFacets'
            class='empty-results-text'>
            Choose filters and apply to view search results
          </div>
          <div v-else-if="!hasSearchResults()" class='empty-results-text'>
            No records meet the criteria specified in your search
          </div>
          <template v-else-if="hasSearchResults()">
            <table class="table table-striped">
              <component
                :is="entityComponents[entityType].searchResultHeader"
                :changeSort="changeSort"
                :getSortClass="getSortClass"
                :getSortIcon="getSortIcon"
              />
              <tbody>
                <template v-for="result in visibleResults">
                  <component
                    :is="entityComponents[entityType].searchResultRow"
                    :result="result"
                    :key="result.id"
                  />
                </template>
              </tbody>
            </table>
            <PaginationControls
              :orderedEntities="searchResults"
              :initialPageSize="initialPageSize"
              :pageSize="pageSize"
              :pageSizeOptions="pageSizeOptions"
              :currentPage="currentPage"
              :firstVisibleEntry="firstVisibleEntry"
              :lastVisibleEntry="lastVisibleEntry"
              :rows="rows"
              :key="currentPage"
              :overallClass="'search-results-pagination'"
              @update-current-page="updateCurrentPage"
              @update-page-size="updatePageSize">
            </PaginationControls>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { LookupModules } from '@/store/moduleConfig';
import api from '@/api';
import ContactPageSearchFacets from '@/components/search/ContactPageSearchFacets.vue';
import ContactSearchResultHeader from '@/components/search/ContactSearchResultHeader.vue';
import ContactSearchResultRow from '@/components/search/ContactSearchResultRow.vue';
import InstrumentSearchFacets from '@/components/search/InstrumentSearchFacets.vue';
import InstrumentSearchResultHeader from '@/components/search/InstrumentSearchResultHeader.vue';
import InstrumentSearchResultRow from '@/components/search/InstrumentSearchResultRow.vue';
import JournalEntryModal from '@/components/contacts/JournalEntryModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import Pagination from '@/mixins/Pagination';
import PaginationControls from '@/components/common/PaginationControls.vue';
import PropertySearchFacets from '@/components/search/PropertySearchFacets.vue';
import PropertySearchResultHeader from '@/components/search/PropertySearchResultHeader.vue';
import PropertySearchResultRow from '@/components/search/PropertySearchResultRow.vue';
import ServiceSearchFacets from '@/components/search/ServiceSearchFacets.vue';
import ServiceSearchResultHeader from '@/components/search/ServiceSearchResultHeader.vue';
import ServiceSearchResultRow from '@/components/search/ServiceSearchResultRow.vue';
import ProjectSearchFacets from '@/components/search/ProjectSearchFacets.vue';
import ProjectSearchResultHeader from '@/components/search/ProjectSearchResultHeader.vue';
import ProjectSearchResultRow from '@/components/search/ProjectSearchResultRow.vue';
import TotalAcresHelper from '../mixins/TotalAcresHelper';
import FileDownloaderHelper from '../mixins/FileDownloaderHelper';

export default {
  name: 'AdvancedSearch',
  components: {
    ContactPageSearchFacets,
    ContactSearchResultHeader,
    ContactSearchResultRow,
    InstrumentSearchFacets,
    InstrumentSearchResultHeader,
    InstrumentSearchResultRow,
    JournalEntryModal,
    LoadingSpinner,
    PaginationControls,
    ProjectSearchFacets,
    ProjectSearchResultHeader,
    ProjectSearchResultRow,
    PropertySearchFacets,
    PropertySearchResultHeader,
    PropertySearchResultRow,
    ServiceSearchFacets,
    ServiceSearchResultHeader,
    ServiceSearchResultRow,
    TotalAcresHelper,
    FileDownloaderHelper,
  },
  mixins: [Pagination, TotalAcresHelper, FileDownloaderHelper],
  data() {
    return {
      serverError: null,
      isExporting: false,
      pageSize: 5,
      currentPage: 1,
      sortAttribute: 'id',
      sortDirection: 'descending',
      visibleResults: [],
      // radioButtonFacetKey is to force a reload of the radio button groups
      // so it does not have a pre-selected item.  We just increment
      // this key so Vue reloads the component
      radioButtonFacetKey: 0,
      entityConverter: {
        Contact: {
          convertRecord: this.convertContact,
        },
        Instrument: {
          convertRecord: this.convertInstrument,
        },
        Project: {
          convertRecord: this.convertProject,
        },
        Property: {
          convertRecord: this.convertProperty,
        },
        Service: {
          convertRecord: this.convertService,
        },
      },
      sortAttributeTypes: {
        id: 'number',
        projectId: 'number',
        instrumentType: 'string',
        staffLeadName: 'string',
        instrumentStatus: 'string',
        localityName: 'string',
        executedDate: 'datetime',
        propertyId: 'string',
        serviceCategory: 'string',
        serviceType: 'string',
        serviceStatus: 'string',
        name: 'string',
        status: 'string',
      },
      entityComponents: {
        Contact: {
          searchFacets: ContactPageSearchFacets,
          searchResultHeader: ContactSearchResultHeader,
          searchResultRow: ContactSearchResultRow,
        },
        Instrument: {
          searchFacets: InstrumentSearchFacets,
          searchResultHeader: InstrumentSearchResultHeader,
          searchResultRow: InstrumentSearchResultRow,
        },
        Project: {
          searchFacets: ProjectSearchFacets,
          searchResultHeader: ProjectSearchResultHeader,
          searchResultRow: ProjectSearchResultRow,
        },
        Property: {
          searchFacets: PropertySearchFacets,
          searchResultHeader: PropertySearchResultHeader,
          searchResultRow: PropertySearchResultRow,
        },
        Service: {
          searchFacets: ServiceSearchFacets,
          searchResultHeader: ServiceSearchResultHeader,
          searchResultRow: ServiceSearchResultRow,
        },
      },
    };
  },
  mounted() {
    // pull query params into search facets
    this.useQueryParamsForSearch();
    this.setUpOrdering();
  },
  computed: {
    ...mapGetters(LookupModules.INSTRUMENT_STATUSES.namespace, {
      getInstrumentStatusName: 'getName',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_TYPES.namespace, {
      getInstrumentTypeProperty: 'getProperty',
    }),
    ...mapGetters(LookupModules.LOCALITIES.namespace, {
      getLocalityName: 'getName',
    }),
    ...mapGetters(LookupModules.PROJECT_STATUSES.namespace, {
      getProjectStatusName: 'getName',
    }),
    ...mapGetters(LookupModules.PROPERTY_STATUSES.namespace, {
      getPropertyStatusName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_CATEGORIES.namespace, {
      getCategoryName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_TYPES.namespace, {
      getTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_STATUSES.namespace, {
      getServiceStatusName: 'getName',
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      getAgreementTypeName: 'getName',
    }),
    entityType: {
      get() {
        return this.$store.state.Search.searchEntityType;
      },
      set(newEntityType) {
        this.$store.dispatch('Search/changeAdvancedSearchEntityType', newEntityType);
        this.$router.push({});
      },
    },
    // not using mapState because we define the namespace at runtime w/ entityType
    searchResults() {
      return this.$store.state.Search.searchResults;
    },
    searchResultsLoading() {
      return this.$store.state.Search.searchResultsLoading;
    },
    selectedSearchFacets() {
      return this.$store.state.Search.selectedSearchFacets;
    },
    facetChangeApplied() {
      return this.$store.state.Search.facetChangeApplied;
    },
    rows() {
      return this.searchResults.length;
    },
    lastAppliedSearchHadNoFacets() {
      return this.$store.state.Search.lastAppliedSearchHadNoFacets;
    },
  },
  watch: {
    searchResults() {
      this.setVisibleResults();
    },
    sortAttribute() {
      this.setVisibleResults();
    },
    sortDirection() {
      this.setVisibleResults();
    },
    entityType() {
      this.currentPage = 1;
      this.setUpOrdering();
    },
    pageSize() {
      this.setVisibleResults();
      this.pushFacetsToQueryString();
    },
    currentPage() {
      this.setVisibleResults();
      this.pushFacetsToQueryString();
    },
  },
  methods: {
    setUpOrdering() {
      // this is just forcing Contact sorting to always be on the name field
      if (this.entityType === 'Contact') {
        this.sortAttribute = 'name';
        this.sortDirection = 'ascending';
      } else {
        this.sortAttribute = 'id';
        this.sortDirection = 'descending';
      }
    },
    async performSearch() {
      this.$store.dispatch(
        'Search/search',
      ).then(() => {
        if (this.entityType === 'Contact') {
          this.$store.dispatch('Contact/setSelectedSearchContacts',
            this.$store.state.Search.searchResults);
        }
      });
    },
    async applySearchCriteria() {
      this.performSearch();
      if (this.hasSelectedSearchFacets()) {
        this.$store.dispatch('Search/setLastAppliedSearchHadNoFacets', false);
      } else {
        this.$store.dispatch('Search/setLastAppliedSearchHadNoFacets', true);
      }
      this.pushFacetsToQueryString();
    },
    clearSearchCriteria() {
      this.$store.dispatch(
        'Search/clearSearchFacets',
      );
      this.pushFacetsToQueryString();
      this.currentPage = 1;
      this.radioButtonFacetKey = this.radioButtonFacetKey + 1;
    },
    exportResults() {
      this.isExporting = true;
      const ids = this.searchResults.map(r => r.id);

      let contentTypeString = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (this.entityType === 'Project') {
        contentTypeString = 'application/zip';
      }

      api.post(`/${this.entityType}/exportsearchresults`, ids, {
        responseType: 'arraybuffer',
      }).then((response) => {
        let defaultFileName = `${this.entityType}SearchResults.xlsx`;
        if (this.entityType === 'Project') {
          defaultFileName = 'ProjectSearchResults.zip';
        }
        const responseFileName = this.getFileNameFromResponseHeader(response, defaultFileName);
        const blob = new Blob([response.data], { type: contentTypeString });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = responseFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.isExporting = false;
      }).catch((error) => {
        console.log(error);
        this.isExporting = false;
      });
    },
    setVisibleResults() {
      // convert results into something we can present on the page and sort appropriately
      let results = this.searchResults
        .map(sr => this.entityConverter[this.entityType].convertRecord(sr));
      if (this.isDescendingOrder()) {
        results = results.sort((a, b) => this.sortByAttribute(b, a, this.sortAttribute));
      } else {
        // see we use the same sort method, but reverse the entries we pass in
        results = results.sort((a, b) => this.sortByAttribute(a, b, this.sortAttribute));
      }
      this.visibleResults = results
        .slice(
          ((this.currentPage - 1) * this.pageSize),
          ((this.currentPage * this.pageSize)),
        );
    },
    convertContact(result) {
      const roles = result.instrumentRoles.sort((a, b) => a.instrumentId - b.instrumentId)
        .map(ci => `Instrument ${ci.instrumentId} - ${ci.name}`)
        .concat(result.propertyRoles.sort((a, b) => a.propertyId - b.propertyId).map(cp => `Property ${cp.propertyId} - ${cp.name}`))
        .concat(result.serviceRoles.sort((a, b) => a.serviceId - b.serviceId).map(cs => `Service ${cs.serviceId} - ${cs.name}`));
      return {
        id: result.crmId,
        name: result.name || result.fullName,
        address: result.address,
        roles,
      };
    },
    convertInstrument(result) {
      return {
        id: result.id,
        projectId: result.projectId,
        instrumentType: this.getInstrumentTypeProperty(result.typeId, 'code'),
        staffLeadName: result.staffLeadName,
        instrumentStatus: this.getInstrumentStatusName(result.statusId),
        localityName: this.getLocalityName(result.localityId),
        executedDate: result.executedDate,
        projectName: result.projectName,
      };
    },
    convertProject(result) {
      return {
        id: result.id,
        name: result.name,
        status: this.getProjectStatusName(result.statusId),
        acres: this.getProjectTotalAcres(result),
        localityName: result.primaryLocality,
      };
    },
    convertProperty(result) {
      return {
        id: result.id,
        projectId: result.projectId,
        name: result.name,
        status: this.getPropertyStatusName(result.statusId),
        localityName: result.propertyLocalities.map(pl => this.getLocalityName(pl.localityId)).sort().join('; '),
        acres: result.calculatedAcres,
      };
    },
    convertService(result) {
      return {
        id: result.id,
        projectId: result.primaryProjectId,
        propertyId: result.propertyIdOrMultiProperty,
        serviceCategory: this.getCategoryName(result.categoryId),
        serviceAgreementType: this.getAgreementTypeName(result.agreementTypeId),
        serviceType: this.getComponentsString(result),
        staffLeadName: result.staffLeadName,
        serviceStatus: this.getServiceStatusName(result.statusId),
        localityName: this.getLocalityName(result.localityId),
      };
    },
    sortByAttribute(a, b, attribute) {
      // propertyId gets special treatment because it can be a number or a string,
      // that is, an id or 'Multi-property'.
      // propertyId gets handled by the code at the end of this method, which also
      // handles regular numbers
      if (this.sortAttributeTypes[attribute] === 'string' && attribute !== 'propertyId') {
        const valueA = a[attribute] === null ? '' : a[attribute].toUpperCase();
        const valueB = b[attribute] === null ? '' : b[attribute].toUpperCase();
        if (valueA < valueB) {
          return -1;
        }
        if (valueA > valueB) {
          return 1;
        }
        // values must be equal
        return 0;
      }
      if (this.sortAttributeTypes[attribute] === 'datetime') {
        return (new Date(a[attribute]) - new Date(b[attribute]));
      }
      if (a[attribute] === b[attribute]) {
        return 0;
      }
      if (Number.isNaN(Number.parseInt(a[attribute], 10))) {
        return 1;
      }
      if (Number.isNaN(Number.parseInt(b[attribute], 10))) {
        return -1;
      }
      return (a[attribute] - b[attribute]);
    },
    pushFacetsToQueryString() {
      const entityTypeObj = {
        entityType: this.entityType,
        currentPage: this.currentPage,
        pageSize: this.pageSize,
      };
      // apply the search facets to the current URL so they can be bookmarked
      // apply object spread operator to append the entityType as the first query param
      const queryObj = {
        query: { ...entityTypeObj, ...this.selectedSearchFacets },
      };
      const objClone = JSON.parse(JSON.stringify(queryObj));
      if (objClone.query.contacts) {
        objClone.query.contacts = queryObj.query.contacts.map(c => c.crmId);
      }
      if (objClone.query.notContacts) {
        objClone.query.notContacts = queryObj.query.notContacts.map(c => c.crmId);
      }
      this.$router.push(objClone);
    },
    async useQueryParamsForSearch() {
      // pull query params into search facets
      const queryParams = Object.entries(this.$route.query);
      if (queryParams.length > 0) {
        let hasSearchFacetParam = false;
        await Promise.all(queryParams.map(async (attributePair) => {
          // eslint-disable-next-line
          let [type, value] = attributePair;
          if (!Array.isArray(value)) {
            if (this.isSearchFacetType(type)) {
              if ((type !== 'eventDateBefore'
                && type !== 'eventDateAfter'
                && !Number.isNaN(parseInt(value, 10)))
               && (type !== 'executedDateBefore'
               && type !== 'executedDateAfter'
               && !Number.isNaN(parseInt(value, 10)))) {
                if (type.toLowerCase().endsWith('acres')) {
                  // arcres are float values, so we don't want to lose precision
                  value = [parseFloat(value)];
                } else {
                  value = [parseInt(value, 10)];
                }
              } else {
                value = [value];
              }
            }
          }
          if (type === 'entityType') {
            await this.$store.dispatch('Search/changeAdvancedSearchEntityType', value);
          } else if (type === 'currentPage') {
            this.currentPage = parseInt(value, 10);
          } else if (type === 'pageSize') {
            this.pageSize = parseInt(value, 10);
          } else {
            hasSearchFacetParam = true;
            await this.$store.dispatch(
              'Search/updateSearchFacet',
              {
                type,
                values: value,
              },
            );
          }
          const eventTypes = ['hasEventType', 'doesNotHaveEventType', 'eventDateBefore', 'eventDateAfter'];
          const contactTypes = ['contacts', 'notContacts', 'roles'];
          const acresTypes = ['minimumAcres', 'maximumAcres'];
          const rightsTypes = ['exceedsAllowedDwellings', 'exceedsAllowedFinishedArea', 'exceedsAllowedImperviousSurface'];
          const executedDateTypes = ['executedDateBefore', 'executedDateAfter'];
          if (eventTypes.indexOf(type) > -1) {
            type = 'event';
          } else if (contactTypes.indexOf(type) > -1) {
            type = 'contact';
          } else if (acresTypes.indexOf(type) > -1) {
            type = 'acres';
          } else if (rightsTypes.indexOf(type) > -1) {
            type = 'rights';
          } else if (executedDateTypes.indexOf(type) > -1) {
            type = 'executedDates';
          }
          await this.$store.dispatch(
            'Search/setSearchCategoryState',
            type,
          );
        }));
        if (hasSearchFacetParam) {
          this.$store.dispatch('Search/setLastAppliedSearchHadNoFacets', false);
        }
      }
      // dispatch actions to populate vuex state for this page
      this.performSearch();
    },
    hasSelectedSearchFacets() {
      if (this.selectedSearchFacets) {
        return Object.entries(this.selectedSearchFacets).flatMap(x => x[1]).length > 0;
      }
      return false;
    },
    hasSearchResults() {
      return this.searchResults && this.searchResults.length > 0;
    },
    changeSort(field) {
      if (this.sortAttribute === field) {
        this.sortDirection = this.isDescendingOrder() ? 'ascending' : 'descending';
      } else {
        this.sortAttribute = field;
        this.sortDirection = 'descending';
      }
      this.updateCurrentPage(1);
    },
    getSortIcon(field) {
      if (this.sortAttribute === field) {
        return this.isDescendingOrder() ? 'sort-down' : 'sort-up';
      }
      return 'sort';
    },
    getSortClass(field) {
      if (this.sortAttribute !== field) {
        return 'search-icon-non-selected-sort';
      }
      return 'search-icon-selected-sort';
    },
    isDescendingOrder() {
      return this.sortDirection === 'descending';
    },
    isSearchFacetType(type) {
      return (type !== 'entityType' && type !== 'currentPage' && type !== 'pageSize');
    },
    getComponentsString(service) {
      if (service && service.typeIds) {
        const typeIdsCopy = [...service.typeIds];
        return typeIdsCopy
          .sort()
          .map(pi => this.getTypeName(pi))
          .join('; ');
      }
      return null;
    },
  },
};
</script>
